/**
 * What a console page can say about itself, for "Report a problem".
 *
 * The same report the room files, from a page with less to say. There are no
 * stream panes here and no player script, so those fields are honestly empty
 * rather than invented — `twitch_embed_loaded: false` on a page that never
 * loads the embed is a true statement, not a symptom.
 *
 * The screenshot and sizing are copied from `apps/rooms/src/lib/diagnostics.ts`
 * rather than imported: apps do not import each other, and there is no shared
 * browser package yet. Keep the two in step — the JPEG cap there is a fix for
 * a production 413, and a copy that lost it would reintroduce it.
 */
import type { BugDiagnosticsShape } from '@tomo/contracts';

/** Read at SEND TIME, never in the background. */
export function collectDiagnostics(win: Window = window): BugDiagnosticsShape {
  return {
    // The PATH, never the query string: a bug report is a thing people
    // forward, and the search string is where a token would land.
    path: win.location.pathname,
    user_agent: win.navigator.userAgent,
    viewport: `${String(win.innerWidth)}x${String(win.innerHeight)}`,
    twitch_embed_loaded: false,
    cookies_enabled: win.navigator.cookieEnabled,
    storage_access: false,
    panes: [],
    errors: [],
    room_id: null,
    app_version: APP_VERSION,
  };
}

/** Which build this is: Vite's fingerprint on the entry bundle. */
const APP_VERSION: string = (() => {
  try {
    const script = document.querySelector<HTMLScriptElement>('script[src*="/assets/index-"]');
    return /index-([A-Za-z0-9_-]+)\.js/.exec(script?.src ?? '')?.[1] ?? 'unknown';
  } catch {
    return 'unknown';
  }
})();

const MAX_SHOT_WIDTH = 1600;
const SHOT_QUALITY = 0.8;

/** The drawn size, never scaled UP — a small window stays its own size. */
export function shotSize(
  width: number,
  height: number,
  max = MAX_SHOT_WIDTH,
): { width: number; height: number } {
  if (!(width > 0) || !(height > 0)) return { width: max, height: Math.round(max * 9 / 16) };
  if (width <= max) return { width: Math.round(width), height: Math.round(height) };
  return { width: max, height: Math.round((height / width) * max) };
}

/**
 * Take a picture of this tab. The browser asks first and the viewer chooses
 * what to share; declining returns null, which is a choice, not a failure.
 */
export async function captureScreenshot(win: Window = window): Promise<string | null> {
  const media = win.navigator.mediaDevices as MediaDevices & {
    getDisplayMedia?: (c: unknown) => Promise<MediaStream>;
  } | undefined;
  if (typeof media?.getDisplayMedia !== 'function') return null;

  let stream: MediaStream | null = null;
  try {
    stream = await media.getDisplayMedia({
      video: { displaySurface: 'browser' },
      audio: false,
      preferCurrentTab: true,
    });
    const track = stream.getVideoTracks()[0];
    if (!track) return null;

    const video = win.document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    await video.play();
    // One frame, once there is one — earlier gives a black rectangle.
    await new Promise((resolve) => { win.requestAnimationFrame(resolve); });

    const size = shotSize(
      video.videoWidth || win.innerWidth,
      video.videoHeight || win.innerHeight,
    );
    const canvas = win.document.createElement('canvas');
    canvas.width = size.width;
    canvas.height = size.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, size.width, size.height);
    return canvas.toDataURL('image/jpeg', SHOT_QUALITY);
  } catch {
    return null;
  } finally {
    // ALWAYS, or the browser keeps saying the tab is being shared.
    stream?.getTracks().forEach((t) => { t.stop(); });
  }
}
